import { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";
import { apiClient } from "../../../../api/httpClient";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import { is_prod, EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import { buildFullName } from "../../../../globals/utils/nameUtils";
import { createSupportRequest } from "../../../../services/supportRequestService";
import logger from "../../../../globals/utils/logger";

/** Matches BlanketBond.jsx step labels */
export const BLANKET_BOND_STEPS = [
  "Basic Information",
  "Detailed Information",
  "Obligee Information",
];

/** Blanket bond_type on `bond` object — from BlanketBond.jsx */
export const BLANKET_BOND_TYPE_ID = 5;

/** Florida state id on bond — from BlanketBond.jsx */
export const BLANKET_BOND_STATE_ID = 6;

const ROLE_HOME_MAP = {
  super_user: "/admin_home",
  internal_user: "/internal_user_home",
  law_firm_user: "/law_firm_user_home",
  law_firm_admin: "/law_firm_admin_home",
  carrier_team_member: "/carrier_team_member_dashboard",
  "professional guardian": "/professional_guardian_home",
  pg_contact: "/pg_contact_home",
};

/**
 * react-hook-form rules aligned with BlanketBond.jsx Controllers.
 * Step 2 obligee is enforced at submit in the page; we require obligeeId on submit in the hook handler.
 */
export const BLANKET_VALIDATION_RULES = {
  firstName: { required: "First name is required" },
  lastName: { required: "Last name is required" },
  dob: { required: "Date of birth is required" },
  county: { required: "County is required" },
  city: { required: true },
  state: { required: true },
  email: { required: true },
  cell_phone: { required: true },
  ssn: { required: true },
  authorizationAgreement: { required: "You must select an option" },
  bankruptExplanation: { required: "Explanation is required" },
  lawsuitsExplanation: { required: "Explanation is required" },
  suretyExplanation: { required: "Explanation is required" },
  bondReplacementReason: { required: "Explanation is required" },
  obligeeId: { required: "Obligee is required" },
};

/** Field names primarily validated on each step (for documentation / optional trigger([...]) use). */
export const BLANKET_STEP_FIELDS = {
  0: [
    "firstName",
    "lastName",
    "dob",
    "county",
    "city",
    "state",
    "email",
    "cell_phone",
    "ssn",
    "authorizationAgreement",
  ],
  1: [
    "bankrupt",
    "bankruptExplanation",
    "lawsuits",
    "lawsuitsExplanation",
    "declinedSurety",
    "suretyExplanation",
    "replacingBond",
    "bondReplacementReason",
    "doe",
    "bondTerm",
  ],
  2: ["obligeeId"],
};

export function calculateBlanketPremium(bondTerm) {
  switch (bondTerm) {
    case "1 year Bond":
    case "1 Year Bond":
      return 175.0;
    case "2 year Bond":
    case "2 Year Bond":
      return 306.0;
    case "3 year Bond":
    case "3 Year Bond":
      return 437.0;
    default:
      return 175.0;
  }
}

export function buildBlanketBondPayload(data, ctx) {
  const {
    isGuestBlanketRequest,
    lawfirm,
    lawfirmState,
    userdetails,
    profGuardianRequestor,
  } = ctx;

  const premium = calculateBlanketPremium(data.bondTerm);

  return {
    bond: {
      bond_carrier: is_prod ? 7 : 4,
      bond_type: BLANKET_BOND_TYPE_ID,
      bond_sub_category: data.bondTerm,
      bond_amount: 50000.0,
      premium,
      state: BLANKET_BOND_STATE_ID,
      county: data.county,
      is_esignatures: data.is_esignatures === "Yes",
      is_guest: isGuestBlanketRequest,
      ...(isGuestBlanketRequest
        ? (() => {
            try {
              const r = JSON.parse(localStorage.getItem("guestReferralData"));
              return r
                ? {
                    referral_source: r.referral_source || null,
                    referred_by: r.referred_by || null,
                  }
                : {};
            } catch {
              return {};
            }
          })()
        : {}),
      status: "Active",
      name: buildFullName(data.firstName, data.middleName, data.lastName),
      law_firm: lawfirm,
      requester_id: isGuestBlanketRequest ? null : userdetails?.id,
      managing_lawfirm: lawfirmState?.lawfirmId,
      requester_name: isGuestBlanketRequest
        ? "Guest"
        : `${userdetails?.first_name ?? ""} ${userdetails?.last_name ?? ""}`.trim(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      obligee: data.obligeeId,
    },
    blanketbond: {
      first_name: data.firstName,
      middle_name: data.middleName,
      last_name: data.lastName,
      preferred_name: data.preferredName,
      street_address_line_1: data.address,
      street_address_line_2: "",
      city: data.city,
      state: data.state,
      zip: data.zipCode,
      cell_phone: data.cell_phone,
      should_we_text: data.mayWeText === "Yes",
      email: data.email,
      dob: data.dob || null,
      date_of_expiration: data.doe || null,
      ssn: data.ssn,
      is_us_citizen: data.usCitizen === "Yes",
      residency_status: data.residencyStatus,
      bankrupcy: data.bankrupt === "Yes",
      bankrupcy_explanation: data.bankruptExplanation,
      judgements: data.lawsuits === "Yes",
      judgements_explanation: data.lawsuitsExplanation,
      is_decline: data.declinedSurety === "Yes",
      declined_explanation: data.suretyExplanation,
      authorization_underwriting: data.authorizationAgreement,
      is_credit_report: data.authorizationAgreement,
      is_replace: data.replacingBond === "Yes",
      surety_name_reason: data.bondReplacementReason,
      bond_term: data.bondTerm,
      pg_id: profGuardianRequestor,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  };
}

function getBlanketFormDefaultValues() {
  return {
    obligeeId: null,
    firstName: "",
    middleName: "",
    lastName: "",
    preferredName: "",
    dob: "",
    county: "",
    address: "",
    zipCode: "",
    city: "",
    state: "",
    email: "",
    is_esignatures: "No",
    mayWeText: "Yes",
    cell_phone: "",
    usCitizen: "Yes",
    ssn: "",
    authorizationAgreement: undefined,
    residencyStatus: "",
    bankrupt: "No",
    bankruptExplanation: "",
    lawsuits: "No",
    lawsuitsExplanation: "",
    declinedSurety: "No",
    suretyExplanation: "",
    replacingBond: "No",
    bondReplacementReason: "",
    doe: "",
    bondTerm: "1 Year Bond",
  };
}

export default function useBlanketBondForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cache_id } = useParams();

  const [isGuestBlanketRequest, setIsGuestBlanketRequest] = useState(() =>
    (location.pathname || "").startsWith("/vermost-blanket-bond-request-guest")
  );
  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cacheId, setCacheId] = useState(cache_id || "");

  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);

  const [otherResidency, setOtherResidency] = useState("");
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [state, setState] = useState(null);
  const [allCounties, setAllCounties] = useState([]);
  const [lawfirm, setLawfirm] = useState(null);
  const [profGuardianRequestor, setProfGuardianRequestor] = useState(null);
  const [obligees, setObligees] = useState([]);
  const [loadingObligees, setLoadingObligees] = useState(false);
  const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
  const [newObligee, setNewObligee] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });

  const [loadingCounties, setLoadingCounties] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [failedBondType, setFailedBondType] = useState("");

  const lawfirmState = useSelector((s) => s.lawfirm);
  const userdetails = useSelector((s) => s.auth.user);
  const { role } = useSelector((s) => s.auth);
  const pgId = useSelector((s) => s.pg.pgId);

  const emailDebounceRef = useRef();

  const form = useForm({
    mode: "onChange",
    defaultValues: getBlanketFormDefaultValues(),
  });

  const { handleSubmit, watch, getValues, setValue, reset, trigger, control, formState } = form;
  const { errors } = formState;

  useEffect(() => {
    const savedPayload = localStorage.getItem("resumedBondPayload");
    if (savedPayload) {
      try {
        const parsed = JSON.parse(savedPayload);
        let body = parsed.request_body;
        if (typeof body === "string") body = JSON.parse(body);
        if (body?.state) reset(body.state);
        if (typeof body.activeStep === "number") setActiveStep(body.activeStep);
        toast.info("Restored bond application from support request.");
        localStorage.removeItem("resumedBondPayload");
      } catch (err) {
        logger.debug("useBlanketBondForm", "resume payload parse failed", { err });
      }
    }
  }, [reset]);

  useEffect(() => {
    if (!cache_id) return;
    (async () => {
      try {
        const data = await apiClient(`/bond-cache/${cache_id}/`);
        let body = data?.request_body;
        if (typeof body === "string") {
          try {
            body = JSON.parse(body);
          } catch (err) {
            logger.debug("useBlanketBondForm", "bond-cache nested body parse failed", { err });
          }
        }
        if (body?.state) reset(body.state);
        if (typeof body?.activeStep === "number") setActiveStep(body.activeStep);
      } catch (err) {
        logger.warn("useBlanketBondForm", "bond-cache load failed", { err });
        toast.error("Failed to load saved progress");
      }
    })();
  }, [cache_id, reset]);

  useEffect(() => {
    const path = location.pathname || "";
    setIsGuestBlanketRequest(path.startsWith("/vermost-blanket-bond-request-guest"));
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  if (!emailDebounceRef.current) {
    emailDebounceRef.current = debounce(async (email) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT);
  }

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [cities, states, obligeesRes] = await Promise.all([
          apiClient("/fields/city/", "GET"),
          apiClient("/fields/states/", "GET"),
          apiClient("/obligee-list-create/"),
        ]);
        setAllCities(cities);
        setAllStates(states);
        setObligees(obligeesRes);
        const defaultObligee = obligeesRes.find(
          (o) => o.obligee_name === "Governor of Florida (Ron Desantis)"
        );
        if (defaultObligee) setValue("obligeeId", defaultObligee.obligee_id);
      } catch (err) {
        logger.warn("useBlanketBondForm", "dropdown data load failed", { err });
        toast.error("Failed to load data");
      }
    };
    loadDropdownData();
    const savedState = localStorage.getItem("selectedState");
    if (savedState) {
      try {
        setState(JSON.parse(savedState));
      } catch (err) {
        logger.debug("useBlanketBondForm", "selectedState JSON parse failed", { err });
      }
    }
  }, [setValue]);

  useEffect(() => {
    const fetchCounties = async () => {
      if (state?.id) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(`/counties-by-state/${state.id}/`, "GET");
          setAllCounties(countyResponse.map((c) => c.bond_county));
        } catch (err) {
          logger.debug("useBlanketBondForm", "counties fetch failed", { err });
        } finally {
          setLoadingCounties(false);
        }
      }
    };
    fetchCounties();
  }, [state]);

  useEffect(() => {
    if (role === "law_firm_user" || role === "law_firm_admin") {
      setLawfirm(lawfirmState.lawfirmId);
    }
  }, [role, lawfirmState.lawfirmId]);

  useEffect(() => {
    if (role === null) setProfGuardianRequestor(pgId);
  }, [role, pgId]);

  const handleOtherResidencyChange = useCallback(
    (e) => {
      const value = e.target.value;
      setOtherResidency(value);
      setValue("residencyStatus", value);
    },
    [setValue]
  );

  const handleGooglePrincipalAddress = useCallback(
    (address, city, stateName, zipCode) => {
      setValue("address", address);
      setValue("city", city);
      setValue("state", stateName);
      setValue("zipCode", zipCode);
    },
    [setValue]
  );

  const handleObligeeGoogleAddress = useCallback(
    (address, city, stateName, zipCode) => {
      setNewObligee((prev) => ({
        ...prev,
        street: address,
        city: allCities.find((c) => c.toLowerCase() === city.toLowerCase()) || city,
        state: allStates.find((s) => s.toLowerCase() === stateName.toLowerCase()) || stateName,
        zip: zipCode,
      }));
    },
    [allCities, allStates]
  );

  const handleEmailFieldChange = useCallback(
    (value, onChange) => {
      onChange(value);
      emailDebounceRef.current(value);
    },
    []
  );

  const handleDialogClose = useCallback(() => setEmailDialogOpen(false), []);
  const handleDialogContinue = useCallback(() => {
    setEmailDialogOpen(false);
    setEmailExists(false);
  }, []);
  const handleDialogCancel = useCallback(() => {
    setEmailDialogOpen(false);
    setValue("email", "");
    setEmailExists(false);
  }, [setValue]);

  const handleAddObligee = useCallback(async () => {
    try {
      setLoadingObligees(true);
      const response = await apiClient("/obligee-list-create/", "POST", {
        obligee_name: newObligee.name,
        obligee_address: newObligee.street,
        obligee_city: newObligee.city,
        obligee_state: newObligee.state,
        obligee_zip: newObligee.zip,
      });
      setObligees((prev) => [...prev, response]);
      setValue("obligeeId", response.obligee_id);
      setOpenObligeeDialog(false);
      setNewObligee({ name: "", street: "", city: "", state: "", zip: "" });
      toast.success("Obligee added successfully!");
    } catch (err) {
      logger.warn("useBlanketBondForm", "add obligee failed", { err });
      toast.error("Failed to add obligee");
    } finally {
      setLoadingObligees(false);
    }
  }, [newObligee, setValue]);

  const navigateRoleHome = useCallback(() => {
    navigate(ROLE_HOME_MAP[role] || "/");
  }, [navigate, role]);

  const onSubmit = useCallback(
    async (data) => {
      if (data.obligeeId == null || data.obligeeId === "") {
        toast.error("Please select an obligee");
        return;
      }
      setIsLoading(true);
      try {
        const payload = buildBlanketBondPayload(data, {
          isGuestBlanketRequest,
          lawfirm,
          lawfirmState,
          userdetails,
          profGuardianRequestor,
        });
        const response = await apiClient("/create-blanket-bond/", "POST", payload);
        localStorage.removeItem("guestReferralData");
        toast.success("Bond created successfully!");
        try {
          const bondId =
            response?.bond_id ||
            response?.id ||
            response?.bond?.bond_id ||
            response?.data?.bond_id;
          if (bondId) {
            sessionStorage.setItem(
              "flashToast",
              JSON.stringify({
                message: `Your Recent Bond Request #${bondId} was Submitted!`,
                link: `/vermost-blanket-bond-update/${bondId}`,
              })
            );
          }
        } catch (err) {
          logger.debug("useBlanketBondForm", "flashToast sessionStorage failed", { err });
        }
        setIsLoading(false);
        reset(getBlanketFormDefaultValues());
        setActiveStep(0);
        navigateRoleHome();
      } catch (error) {
        const message = extractErrorMessage(error);
        setSubmissionError(message);
        setFailedBondType("Blanket");
        setSupportModalOpen(true);
        setIsLoading(false);
      }
    },
    [
      isGuestBlanketRequest,
      lawfirm,
      lawfirmState,
      userdetails,
      profGuardianRequestor,
      navigateRoleHome,
      reset,
    ]
  );

  const handleFormSubmit = handleSubmit(onSubmit);

  const handleNext = useCallback(
    async (event) => {
      event?.preventDefault();
      const ok = await trigger();
      if (!ok) return;
      setActiveStep((p) => p + 1);
    },
    [trigger]
  );

  const handleBack = useCallback((event) => {
    event?.preventDefault();
    setActiveStep((p) => p - 1);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      if (!userdetails?.id) {
        toast.error("Unable to save: Login to save your progress");
        return;
      }
      const values = getValues();
      const name = buildFullName(values.firstName, values.middleName, values.lastName);
      const saved = await apiClient("/bond-cache/", "POST", {
        cache_id: cacheId || cache_id,
        requester_id: userdetails.id,
        bond_type: "Blanket",
        name: name || "Untitled Blanket Bond",
        request_body: JSON.stringify({ activeStep, state: values }),
      });
      const returnedId = saved?.bond_cache_id || saved?.cache_id || saved?.id;
      if (returnedId) {
        if (returnedId !== cacheId) setCacheId(returnedId);
        if (!cache_id) {
          navigate(`/vermost-blanket-bond-request-cache/${returnedId}`, { replace: true });
        }
      }
      toast.success("Progress saved");
    } catch (error) {
      toast.error((error && (error.detail || error.error)) || "Failed to save progress");
    } finally {
      setIsSaving(false);
    }
  }, [activeStep, cacheId, cache_id, getValues, navigate, userdetails?.id]);

  const handleSupportRequestSubmit = useCallback(
    async (description) => {
      try {
        const values = getValues();
        await createSupportRequest({
          request_body: JSON.stringify({ activeStep, state: values }),
          bond_type: failedBondType,
          name: buildFullName(values.firstName, values.middleName, values.lastName) || "Unknown",
          user_comment: description,
        });
        toast.success("Support request created successfully.");
        setSupportModalOpen(false);
        navigateRoleHome();
      } catch (err) {
        logger.debug("useBlanketBondForm", "support request failed", { err });
        toast.error("Failed to submit support request.");
      }
    },
    [activeStep, failedBondType, getValues, navigateRoleHome]
  );

  return {
    steps: BLANKET_BOND_STEPS,
    activeStep,
    setActiveStep,

    isGuestBlanketRequest,
    isLoading,
    isSaving,
    cacheId,

    openCalculator,
    setOpenCalculator,
    openSupport,
    setOpenSupport,

    supportModalOpen,
    setSupportModalOpen,
    submissionError,
    failedBondType,

    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    trigger,
    errors,
    handleFormSubmit,

    allCities,
    allStates,
    allCounties,
    state,
    setState,
    loadingCounties,
    obligees,
    loadingObligees,
    openObligeeDialog,
    setOpenObligeeDialog,
    newObligee,
    setNewObligee,

    lawfirm,
    profGuardianRequestor,
    lawfirmState,
    userdetails,
    role,

    otherResidency,
    handleOtherResidencyChange,

    emailExists,
    emailChecking,
    emailDialogOpen,
    handleEmailDialogClose: handleDialogClose,
    handleEmailDialogContinue: handleDialogContinue,
    handleEmailDialogCancel: handleDialogCancel,
    handleEmailFieldChange,

    handleGooglePrincipalAddress,
    handleObligeeGoogleAddress,
    handleAddObligee,

    handleNext,
    handleBack,
    handleSave,
    handleSupportRequestSubmit,
    onSubmit,

    buildPayload: (data) =>
      buildBlanketBondPayload(data, {
        isGuestBlanketRequest,
        lawfirm,
        lawfirmState,
        userdetails,
        profGuardianRequestor,
      }),
    roleHomePath: ROLE_HOME_MAP[role] || "/",
  };
}
